import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, Subscription } from 'rxjs';
import { catchError, concatMap, tap } from 'rxjs/operators';

import { BaseSortableSearchComponent } from '@components/_base/base-search/base-sortable-search.component';
import { displayError } from '@app/_utils/error-util';
import { PermissionsService } from '@app/login/_services/permissions.service';
import { TechnicalPointService } from '@app/tech-points/_services/technical-point.service';
import { TechnicalPoint } from '@app/tech-points/_models/technical-point.model';

import { SmsCampaignConfig } from '../_models/sms-campaign-config.model';
import { SmsCampaignConfigService } from '../_services/sms-campaign-config.service';
import { INCOMING_WAYS, incomingWayNameKey } from '../_enums/incoming-way.enum';

@Component({
  selector: 'app-list-sms-campaigns',
  templateUrl: './list-sms-campaigns.component.html',
  styleUrls: ['./list-sms-campaigns.component.css']
})
export class ListSmsCampaignsComponent extends BaseSortableSearchComponent<SmsCampaignConfig> implements OnInit, OnDestroy {

  readonly incomingWays = INCOMING_WAYS;

  technicalPoints: TechnicalPoint[] = [];

  searchForm = this.fb.group({
    name:                      [null],
    technicalPointMobileAppId: [null],
    isActive:                  [null],
    incomingWay:               [null]
  });

  searchSubscription: Subscription;

  search$ = this.searchSubject.asObservable().pipe(
    tap(() => this.dataLoaded = false),
    concatMap(() =>
      this.smsCampaignConfigService.pageable(this.sortingPaging, this.searchForm.value).pipe(
        tap(([page, totalCount]) => {
          this.sortingPaging.fromRow = page.fromRow;
          this.sortingPaging.toRow = page.toRow;
          this.sortingPaging.totalElements = totalCount;
          this.content = page.content;
          this.dataLoaded = true;
        })
      )
    ),
    catchError(err => {
      displayError(err);
      this.dataLoaded = false;
      this.errorMessageSubject.next(this.translateService.instant('messages.errorLoadingData'));
      return EMPTY;
    })
  );

  constructor(
    private fb:                       FormBuilder,
    private translateService:         TranslateService,
    private technicalPointService:    TechnicalPointService,
    private smsCampaignConfigService: SmsCampaignConfigService,
    public  perms:                    PermissionsService,
  ) {
    super();
  }

  ngOnInit() {
    this.technicalPointService.findTechnicalPoints().subscribe({
      next: (data) => this.technicalPoints = (data ?? []).filter(tp => tp.mobileAppId != null),
      error: (err) => displayError(err)
    });

    this.searchSubscription = this.search$.subscribe();
    this.searchSubject.next();
  }

  ngOnDestroy() {
    this.searchSubscription?.unsubscribe();
  }

  technicalPointNames(config: SmsCampaignConfig): string {
    return (config.technicalPoints ?? [])
      .map(tp => tp.name)
      .filter(name => !!name)
      .join(', ');
  }

  incomingWayLabels(config: SmsCampaignConfig): string {
    return (config.incomingWays ?? [])
      .map(code => {
        const key = incomingWayNameKey(code);
        return key ? this.translateService.instant(key) : code;
      })
      .join(', ');
  }
}
